import React, { useState, useCallback, ReactNode } from 'react';

export interface Tab {
  id: string;
  label: string;
  disabled?: boolean;
  icon?: ReactNode;
  badge?: string | number;
}

export interface TabsProps {
  tabs: Tab[];
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  variant?: 'default' | 'compact';
  className?: string;
  children?: ReactNode;
}

export const Tabs: React.FC<TabsProps> = ({
  tabs,
  activeTab: controlledActiveTab,
  onTabChange,
  variant = 'default',
  className = '',
  children
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState(
    tabs.length > 0 ? tabs[0].id : ''
  );
  
  const activeTab = controlledActiveTab !== undefined ? controlledActiveTab : internalActiveTab;
  
  const handleTabClick = useCallback((tabId: string) => {
    const tab = tabs.find(t => t.id === tabId);
    if (tab && !tab.disabled) {
      if (controlledActiveTab === undefined) {
        setInternalActiveTab(tabId);
      }
      onTabChange?.(tabId);
    }
  }, [tabs, controlledActiveTab, onTabChange]);
  
  return (
    <div className={`ui-tabs ${variant === 'compact' ? 'ui-tabs--compact' : ''} ${className}`}>
      <div className="ui-tabs-list" role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-disabled={tab.disabled}
            className={`ui-tabs-tab ${activeTab === tab.id ? 'ui-tabs-tab--active' : ''} ${
              tab.disabled ? 'ui-tabs-tab--disabled' : ''
            }`}
            onClick={() => handleTabClick(tab.id)}
            disabled={tab.disabled}
            type="button"
          >
            {tab.icon && <span className="ui-tabs-tab-icon">{tab.icon}</span>}
            <span className="ui-tabs-tab-content">
              {/* Invisible spacer for width calculation with bold text */}
              <span className="ui-tabs-tab-spacer" aria-hidden="true">
                {tab.label}
              </span>
              {/* Visible tab text */}
              <span className="ui-tabs-tab-label">{tab.label}</span>
            </span>
            {tab.badge !== undefined && (
              <span className="ui-tabs-tab-badge">{tab.badge}</span>
            )}
          </button>
        ))}
      </div>
      {children && (
        <div className="ui-tabs-content" role="tabpanel">
          {children}
        </div>
      )}
    </div>
  );
};

export default Tabs;
